import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import HairpacaAPIContent from '@/components/HairpacaAPI';
import CustomHead from '@/components/Common/CustomHead';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd } from '@/lib/jsonld';
import * as React from 'react';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;
  const localeRes = await serverSideTranslations(locale, ['common']);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const tdkData = await getTdkApi(resolvedUrl, locale);

  const jsonLd = page2JsonLd(
    {
      title: tdkData?.data?.title ?? tempData[`meta_header_HairpacaAPI_Title`],
      desc: tdkData?.data?.description ?? tempData[`meta_header_HairpacaAPI_Desc`],
      url: canonicalUrl(req.headers.host, resolvedUrl, locale)
    },
    {
      url: canonicalUrl(req.headers.host, '/', locale)
    }
  );
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      title: tdkData?.data?.title ?? tempData[`meta_header_HairpacaAPI_Title`],
      description: tdkData?.data?.description ?? tempData[`meta_header_HairpacaAPI_Desc`],
      jsonLd
    }
  };
};

const IndexPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = () => {
  return <HairpacaAPIContent />;
};

IndexPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <CustomHead>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />

          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </CustomHead>
      }
    >
      {page}
    </Layout>
  );
};

export default IndexPage;
